import React from "react";
import { Box } from "@mui/joy";
import { PersonOffIcon } from "../../styles/StyledMUI";
import { useRgpd } from "../../contexts/api";
import { useConfirmationModal } from "../../contexts/app/confirmation";
import { useCompanyIdFromSearch } from "../../contexts/app/useCompanyIdFromSearch";
import { zekayStaticBranding } from "../../config/staticFiles";
import { textTokens } from "../../styles/globalStyles";
import DarkInput from "../common/DarkInput";
import DarkTypography from "../common/DarkTypography";
import InvalidLinkCard from "../common/InvalidLinkCard";
import IconButtonCTA from "../common/IconButtonCTA";

export default function UnregisterPage() {
  const [email, setEmail] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [done, setDone] = React.useState(false);

  const { unregisterNewsletter } = useRgpd();
  const { showConfirmation } = useConfirmationModal();
  const zekayIcon = zekayStaticBranding.zekayIconPng();
  const companyId = useCompanyIdFromSearch();

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value.toLowerCase());
  };

  const handleUnregister = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;

    const confirmed = await showConfirmation(
      "Désinscription newsletter",
      "Confirmez-vous la désinscription de cette adresse email ? Vous ne recevrez plus nos communications.",
    );
    if (!confirmed) return;

    setIsLoading(true);
    try {
      await unregisterNewsletter(trimmed, companyId);
      setDone(true);
    } finally {
      setIsLoading(false);
    }
  };

  if (!Number.isFinite(companyId) || companyId <= 0) {
    return (
      <InvalidLinkCard message="Ce lien est invalide, veuillez utiliser le lien de désinscription de la newsletter pour ne plus recevoir nos communications." />
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: { xs: 2, sm: 3 },
        paddingTop: { xs: 2, sm: 4 },
        backgroundColor: "background.body",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: { xs: "100%", sm: 400, md: 500 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 3,
        }}
      >
        <Box sx={{ textAlign: "center", mb: 1.5 }}>
          <img
            src={zekayIcon}
            width="80px"
            height="auto"
            alt="Zekay"
            style={{
              filter:
                "brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(7500%) hue-rotate(225deg) brightness(104%) contrast(104%)",
              opacity: 0.9,
            }}
          />
        </Box>

        <DarkTypography
          level="body-md"
          colorVariant="secondary"
          sx={{
            textAlign: "center",
            mb: 2,
            maxWidth: "350px",
            lineHeight: 1.4,
            fontSize: "14px",
          }}
        >
          {done
            ? "Votre demande de désinscription à la newsletter a été effectuée."
            : "Entrez votre adresse email pour vous désinscrire de la newsletter."}
        </DarkTypography>

        {!done && (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              width: "100%",
              alignItems: "center",
            }}
          >
            <DarkInput
              placeholder="Votre adresse email"
              value={email}
              onChange={handleEmailChange}
              onKeyPress={(e: React.KeyboardEvent) => e.key === "Enter" && handleUnregister()}
              size="lg"
              sx={{
                flex: 1,
                "--Input-minHeight": "48px",
                fontSize: "16px",
                textAlign: "center",
              }}
              disabled={isLoading}
            />
            <IconButtonCTA
              onClick={handleUnregister}
              disabled={isLoading}
              size="lg"
              ctaVariant="accent"
              aria-label="Se desinscrire de la newsletter"
              sx={{
                minHeight: "48px",
                minWidth: "48px",
                "&:disabled": {
                  backgroundColor: textTokens.color.dimmed,
                  color: textTokens.color.muted,
                },
              }}
            >
              <PersonOffIcon />
            </IconButtonCTA>
          </Box>
        )}
      </Box>
    </Box>
  );
}
